import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Zap, Loader2, ArrowLeft, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import api from "@/lib/api";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = (searchParams.get("token") || "").trim();
  const navigate = useNavigate();
  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setValidating(false);
      setValid(false);
      return;
    }
    api
      .get("/auth/password-reset/validate", { params: { token } })
      .then((r) => setValid(!!r.data?.valid))
      .catch(() => setValid(false))
      .finally(() => setValidating(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== password2) {
      setError("A két jelszó nem egyezik.");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setError("");
      if (r.data?.message) {
        setTimeout(() => navigate("/belepes"), 2500);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Nem sikerült frissíteni a jelszót.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dark min-h-screen bg-anthracite-900 text-white flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <Link
          to="/belepes/jelszo-elfelejtve"
          data-testid="reset-back-forgot"
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Új link kérése
        </Link>

        <div className="glass rounded-3xl p-8">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-6 h-6 text-electric" strokeWidth={1.5} />
            <span className="font-heading text-xl font-semibold">
              <BrandMark />
            </span>
          </div>
          <h1 className="font-heading text-2xl font-light mt-4">Új jelszó</h1>

          {validating && (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-electric animate-spin" />
            </div>
          )}

          {!validating && !valid && !done && (
            <div data-testid="reset-invalid" className="mt-6 space-y-4">
              <p className="text-sm text-red-300">
                Érvénytelen vagy lejárt a link. Kérj újat az email címedre.
              </p>
              <Link
                to="/belepes/jelszo-elfelejtve"
                className="inline-flex text-sm text-electric hover:underline"
              >
                Visszaállító email küldése →
              </Link>
            </div>
          )}

          {done && (
            <div
              data-testid="reset-success"
              className="mt-6 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-100"
            >
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-400" />
                <p>
                  A jelszavad frissítve. Átirányítunk a belépéshez…
                </p>
              </div>
            </div>
          )}

          {!validating && valid && !done && (
            <form onSubmit={submit} className="space-y-4 mt-6">
              <p className="text-slate-400 text-sm">Legalább 8 karakteres jelszót adj meg.</p>
              <PasswordInput
                testid="reset-password"
                label="Új jelszó"
                value={password}
                onChange={setPassword}
                show={show}
                onToggle={() => setShow((s) => !s)}
              />
              <PasswordInput
                testid="reset-password-confirm"
                label="Új jelszó még egyszer"
                value={password2}
                onChange={setPassword2}
                show={show}
                onToggle={() => setShow((s) => !s)}
              />
              {error && (
                <p data-testid="reset-error" className="text-sm text-red-400">
                  {error}
                </p>
              )}
              <button
                data-testid="reset-submit"
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-electric hover:bg-electric-light disabled:opacity-50 text-white font-medium transition-all"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                Jelszó mentése
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function PasswordInput({ testid, label, value, onChange, show, onToggle }) {
  return (
    <div>
      <label className="text-xs tracking-[0.15em] uppercase text-slate-400 block mb-2">{label}</label>
      <div className="relative">
        <input
          data-testid={testid}
          type={show ? "text" : "password"}
          required
          minLength={8}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          className="w-full bg-white/[0.03] border border-white/10 rounded-xl p-3 pr-11 text-sm text-white focus:border-electric outline-none transition-colors"
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-slate-400 hover:text-white"
          aria-label={show ? "Jelszó elrejtése" : "Jelszó megjelenítése"}
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
